import { useCallback, useEffect, useState } from "react";
import { useDisclosure } from "@chakra-ui/hooks";
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from "@chakra-ui/modal";
import { Button, ChakraProvider, Input, FormControl, FormLabel } from "@chakra-ui/react";
import Loader from "components/loader/loader";
import Navbar from "../../../components/navbar";
import { t } from "i18next";
import { errorToast, successToast } from "utils/commons";
import {
    getAllAdminsApi,
    createAdminApi,
    updateAdminApi,
    deleteAdminApi,
    AdminData,
} from "../../../services/customAPI";
import { MdEdit, MdVisibility, MdDelete } from "react-icons/md";

type ModalMode = "view" | "edit" | "create" | "delete" | null;

const Admins = () => {
    const { isOpen, onOpen, onClose: onDisclosureClose } = useDisclosure();

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [admins, setAdmins] = useState<AdminData[]>([]);
    const [modalMode, setModalMode] = useState<ModalMode>(null);
    const [selectedAdmin, setSelectedAdmin] = useState<AdminData | null>(null);

    // Form state for create/edit
    const [formData, setFormData] = useState({ name: "", email: "", password: "" });

    const onClose = () => {
        onDisclosureClose();
        setModalMode(null);
        setSelectedAdmin(null);
        setFormData({ name: "", email: "", password: "" });
    };

    const fetchAdmins = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllAdminsApi();
            if (response?.data) {
                setAdmins(response.data);
            }
        } catch (error) {
            console.error("Error fetching admins:", error);
            errorToast(t("Erro ao buscar administradores"));
        } finally {
            setLoading(false);
        }
    }, []);

    const openModal = (mode: ModalMode, admin?: AdminData) => {
        setModalMode(mode);
        if (admin) {
            setSelectedAdmin(admin);
            if (mode === "edit") {
                setFormData({ name: admin.name || "", email: admin.email || "", password: "" });
            }
        } else {
            setFormData({ name: "", email: "", password: "" });
        }
        onOpen();
    };

    const handleCreate = async () => {
        if (!formData.name || !formData.email || !formData.password) {
            errorToast(t("Preencha todos os campos"));
            return;
        }
        try {
            setSaving(true);
            await createAdminApi(formData);
            successToast(t("Administrador criado com sucesso"));
            onClose();
            fetchAdmins();
        } catch (error: any) {
            errorToast(error?.response?.data?.message || t("Erro ao criar administrador"));
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async () => {
        if (!selectedAdmin?.id) return;
        if (!formData.name || !formData.email) {
            errorToast(t("Nome e email são obrigatórios"));
            return;
        }
        try {
            setSaving(true);
            const updatePayload: Partial<AdminData> & { password?: string } = {
                name: formData.name,
                email: formData.email,
            };
            if (formData.password.trim()) {
                updatePayload.password = formData.password;
            }
            await updateAdminApi(selectedAdmin.id, updatePayload);
            successToast(t("Administrador atualizado com sucesso"));
            onClose();
            fetchAdmins();
        } catch (error: any) {
            errorToast(error?.response?.data?.message || t("Erro ao atualizar administrador"));
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedAdmin?.id) return;
        try {
            setSaving(true);
            await deleteAdminApi(selectedAdmin.id);
            successToast(t("Administrador excluído com sucesso"));
            onClose();
            fetchAdmins();
        } catch (error) {
            errorToast(t("Erro ao excluir administrador"));
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, [fetchAdmins]);

    const renderModalContent = () => {
        switch (modalMode) {
            case "view":
                return (
                    <>
                        <ModalHeader>{t("Detalhes do Administrador")}</ModalHeader>
                        <ModalBody>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm font-semibold text-gray-500">{t("Nome")}</p>
                                    <p className="text-lg">{selectedAdmin?.name}</p>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-500">{t("Email")}</p>
                                    <p className="text-lg">{selectedAdmin?.email}</p>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-500">{t("Criado em")}</p>
                                    <p className="text-lg">
                                        {selectedAdmin?.createdAt
                                            ? new Date(selectedAdmin.createdAt).toLocaleString("pt-BR")
                                            : "-"}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-500">{t("Atualizado em")}</p>
                                    <p className="text-lg">
                                        {selectedAdmin?.updatedAt
                                            ? new Date(selectedAdmin.updatedAt).toLocaleString("pt-BR")
                                            : "-"}
                                    </p>
                                </div>
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" onClick={onClose}>
                                {t("Fechar")}
                            </Button>
                            <Button
                                colorScheme="blue"
                                ml={3}
                                onClick={() => {
                                    onDisclosureClose();
                                    setTimeout(() => openModal("edit", selectedAdmin!), 150);
                                }}
                            >
                                {t("Editar")}
                            </Button>
                        </ModalFooter>
                    </>
                );

            case "edit":
                return (
                    <>
                        <ModalHeader>{t("Editar Administrador")}</ModalHeader>
                        <ModalBody>
                            <FormControl mb={3}>
                                <FormLabel>{t("Nome")}</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder={t("Nome completo") as string}
                                />
                            </FormControl>
                            <FormControl mb={3}>
                                <FormLabel>{t("Email")}</FormLabel>
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="email@exemplo.com"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>{t("Nova Senha")} ({t("opcional")})</FormLabel>
                                <Input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    placeholder={t("Deixe vazio para manter a atual") as string}
                                />
                            </FormControl>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onClose}>
                                {t("Cancelar")}
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={handleUpdate}
                                isLoading={saving}
                                loadingText={t("Salvando...") as string}
                            >
                                {t("Salvar")}
                            </Button>
                        </ModalFooter>
                    </>
                );

            case "create":
                return (
                    <>
                        <ModalHeader>{t("Novo Administrador")}</ModalHeader>
                        <ModalBody>
                            <FormControl mb={3}>
                                <FormLabel>{t("Nome")}</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder={t("Nome completo") as string}
                                />
                            </FormControl>
                            <FormControl mb={3}>
                                <FormLabel>{t("Email")}</FormLabel>
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="email@exemplo.com"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>{t("Senha")}</FormLabel>
                                <Input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    placeholder={t("Mínimo 6 caracteres") as string}
                                />
                            </FormControl>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onClose}>
                                {t("Cancelar")}
                            </Button>
                            <Button
                                colorScheme="green"
                                onClick={handleCreate}
                                isLoading={saving}
                                loadingText={t("Criando...") as string}
                            >
                                {t("Criar Admin")}
                            </Button>
                        </ModalFooter>
                    </>
                );

            case "delete":
                return (
                    <>
                        <ModalHeader>{t("Confirmar Exclusão")}</ModalHeader>
                        <ModalBody>
                            {t("Deseja realmente excluir o administrador")} "<strong>{selectedAdmin?.name}</strong>"?
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onClose}>
                                {t("Cancelar")}
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleDelete}
                                isLoading={saving}
                                loadingText={t("Excluindo...") as string}
                            >
                                {t("Excluir")}
                            </Button>
                        </ModalFooter>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <div>
            <Navbar flag={false} brandText={t("Administradores")} />

            {loading ? (
                <Loader />
            ) : (
                <div className="mt-4 px-4">
                    {/* Header with Add button */}
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-white">
                            {t("Lista de Administradores")}
                        </h2>
                        <button
                            onClick={() => openModal("create")}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                            + {t("Adicionar Admin")}
                        </button>
                    </div>

                    {/* Admin Table */}
                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden shadow-lg">
                        <table className="w-full">
                            <thead className="bg-navy-900/50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-300">
                                        {t("Nome")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-300">
                                        {t("Email")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-bold text-gray-300">
                                        {t("Criado em")}
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-300">
                                        {t("Ações")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {admins.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                                            {t("Nenhum administrador encontrado")}
                                        </td>
                                    </tr>
                                ) : (
                                    admins.map((admin) => (
                                        <tr
                                            key={admin.id}
                                            className="border-t border-white/5 hover:bg-white/5 transition-colors"
                                        >
                                            <td className="px-4 py-3">
                                                <div className="flex items-center">
                                                    <div className="w-9 h-9 bg-brand-500 rounded-full flex items-center justify-center mr-3 text-white font-bold text-sm">
                                                        {(admin.name || "A").charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className="font-medium text-white">
                                                        {admin.name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-300">
                                                {admin.email}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-400">
                                                {admin.createdAt
                                                    ? new Date(admin.createdAt).toLocaleDateString("pt-BR")
                                                    : "-"}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => openModal("view", admin)}
                                                        className="p-2 text-blue-400 hover:bg-blue-900/20 rounded-lg transition-colors"
                                                        title={t("Visualizar") as string}
                                                    >
                                                        <MdVisibility size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => openModal("edit", admin)}
                                                        className="p-2 text-amber-400 hover:bg-amber-900/20 rounded-lg transition-colors"
                                                        title={t("Editar") as string}
                                                    >
                                                        <MdEdit size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => openModal("delete", admin)}
                                                        className="p-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title={t("Excluir") as string}
                                                    >
                                                        <MdDelete size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Single Modal for all operations */}
            <ChakraProvider>
                <Modal isOpen={isOpen} onClose={onClose} isCentered size={modalMode === "delete" ? "sm" : "md"}>
                    <ModalOverlay />
                    <ModalContent>
                        {renderModalContent()}
                    </ModalContent>
                </Modal>
            </ChakraProvider>
        </div>
    );
};

export default Admins;
